import type { ContentMedia as ContentMediaType } from "@nnf3d/studio/types";
import type { PortableTextTypeComponentProps } from "@portabletext/react";

import type { ParentComponent } from "../../lib/types";
import { imageUrlBuilder } from "../../lib/utils";

const toEmbedUrl = (url: string): string | null => {
	try {
		const parsed = new URL(url);

		if (parsed.hostname === "vimeo.com" || parsed.hostname === "www.vimeo.com") {
			const id = parsed.pathname.split("/").findLast(Boolean);
			return id ? `https://player.vimeo.com/video/${id}` : null;
		}

		if (parsed.hostname === "player.vimeo.com") {
			return url;
		}

		return url;
	} catch {
		return null;
	}
};

const ContentMedia: ParentComponent<PortableTextTypeComponentProps<ContentMediaType>> = ({
	value,
}) => {
	const dpr = Math.min(globalThis.devicePixelRatio || 2, 2);
	const width = 896;
	const height = Math.round((width / 16) * 9);
	const imageUrl = value.image?.asset?._ref
		? imageUrlBuilder(value.image)
				.dpr(dpr)
				.fit("fillmax")
				.width(width)
				.height(height)
				.quality(90)
				.auto("format")
				.url()
		: null;

	const embedUrl = value.videoUrl ? toEmbedUrl(value.videoUrl) : null;
	const iframeTitle = value.image?.alt ?? "Video";

	return (
		<figure className="flex flex-col gap-3 not-last:mb-7">
			{imageUrl && (
				<img
					src={imageUrl}
					alt={value.image?.alt ?? ""}
					className="aspect-video w-full bg-surface-primary object-cover"
					width={width}
					height={height}
					loading="eager"
				/>
			)}

			{embedUrl && (
				<div className="relative aspect-video w-full overflow-hidden">
					{/* oxlint-disable-next-line iframe-missing-sandbox */}
					<iframe
						title={iframeTitle}
						src={embedUrl}
						className="absolute inset-0 size-full"
						allow="autoplay; fullscreen; picture-in-picture"
						allowFullScreen
					/>
				</div>
			)}

			{value.caption && (
				<figcaption className="font-sans text-xs text-foreground/70">
					{value.captionLink ? (
						<a href={value.captionLink} target="_blank" className="underline hover:opacity-70">
							{value.caption}
						</a>
					) : (
						value.caption
					)}
				</figcaption>
			)}
		</figure>
	);
};

export { ContentMedia };
